import lottie, { AnimationEventName, AnimationItem } from 'lottie-web';
import { Action, Dispatch } from 'redux';
import LottieError from './LottieError';
import { LottieActions } from './slice';

interface IAnimationItem extends AnimationItem {
  currentFrame: number;
}

export interface ILottieMap {
  [name: string]: IAnimationItem;
}

export interface ILottieFrameEvent {
  type: AnimationEventName;
  currentTime: number;
  totalTime: number;
  direction: number;
}

export class LottieControllerClass {
  private lottieMap: ILottieMap;
  private dispatch?: Dispatch<Action<string>>;

  constructor() {
    this.lottieMap = {};
  }

  setup(dispatch: Dispatch) {
    this.dispatch = dispatch;
  }

  private containerIsEmpty(container: HTMLElement): boolean {
    return !container.getElementsByTagName('svg').length;
  }

  private lottieNotSavedBefore(id: string): boolean {
    return !this.lottieMap[id];
  }

  private isNewLottie(id: string, container: HTMLElement): boolean {
    return this.lottieNotSavedBefore(id) || this.containerIsEmpty(container);
  }

  load(id: string, animationData: any, autoplay = false, loop = false, dontKeep = false, stopOn = 0) {
    const container = document.getElementById(id);
    if (!container) {
      // container for animation was already destroyed - nothing to do
      return;
    }

    if (this.isNewLottie(id, container)) {
      // drop the instance left over from a previous mount or from a changed id
      this.destroy(id);
      const newItem = lottie.loadAnimation({
        name: id,
        container,
        autoplay,
        loop,
        animationData,
        renderer: 'svg',
      });
      this.lottieMap[id] = newItem;
      // listeners are bound once per animation item, a repeated load would duplicate them otherwise
      newItem.addEventListener('DOMLoaded', this.loadedHandler(id));
      newItem.addEventListener('complete', this.completeHandler(id, dontKeep));
      if (stopOn) {
        newItem.addEventListener('enterFrame', (event: ILottieFrameEvent) => this.stopAtHandler(event, stopOn, id));
      }
    }
    const item = this.lottieMap[id];
    if (stopOn) {
      // make animation slower when duration reduced
      item.setSpeed(stopOn / item.getDuration(true));
      if (item.currentFrame !== undefined && item.currentFrame < stopOn) {
        item.play();
      }
    }
  }

  destroy = (id: string) => {
    const item = this.lottieMap[id];
    if (!item) {
      return;
    }
    // destroys the animation item, clears its (possibly detached) container and unregisters
    // it from the lottie-web global registry through the 'destroy' event
    item.destroy();
    delete this.lottieMap[id];
  };

  stopAtHandler = (event: ILottieFrameEvent, frame: number, id: string) => {
    // currentTime is decimal number and can skip some frames, therefore ">" check is necessary
    if (event.currentTime > frame) {
      this.lottieMap[id].pause();
    }
  };

  loadedHandler = (id: string) => () => {
    if (!this.dispatch) {
      throw new LottieError('LottieController not set up properly!');
    }
    this.dispatch(LottieActions.lottieLoaded(id));
  };

  completeHandler = (id: string, dontKeep: boolean) => () => {
    if (!this.dispatch) {
      throw new LottieError('LottieController not set up properly!');
    }
    this.dispatch(LottieActions.lottieComplete(id));
    if (dontKeep) {
      this.destroy(id);
    }
  };

  play = (id: string) => {
    lottie.play(id);
  };
}

export const LottieController = new LottieControllerClass();
export default LottieController;
